/**
 * دیتافید تریدینگ‌ویو
 * ====================
 * پلی بین کتابخانه نموداری تریدینگ‌ویو و API خودمان.
 *
 * چرا خودمان نوشتیم و از `UDFCompatibleDatafeed` آماده استفاده نکردیم:
 * آن آداپتور در بسته‌ی جداگانه‌ای (`datafeeds/udf/dist/bundle.js`) است که
 * باید کنار کتابخانه کپی شود. یک فایلِ جاافتاده یعنی نمودار سفید و پیام
 * مبهم در کنسول. این نسخه هیچ فایل اضافه‌ای لازم ندارد و رفتارش هم دست
 * خودمان است — مثلاً هر پنج ثانیه فقط کندلِ در جریان را تازه می‌کند، نه کل
 * تاریخچه را.
 *
 * دو تله‌ای که در این قرارداد آدم را زمین می‌زند و اینجا رعایت شده:
 *  • زمان روی HTTP بر حسب **ثانیه** است ولی در جاوااسکریپت بر حسب
 *    **میلی‌ثانیه**. جابه‌جا کردنشان یعنی نمودارِ سالِ ۱۹۷۰.
 *  • کندلِ تازه هرگز نباید زمانی **قبل‌تر** از کندل فعلی داشته باشد؛
 *    کتابخانه در آن حالت داده را دور می‌ریزد و نمودار می‌ایستد.
 */
const BASE = import.meta.env.VITE_API_URL || ''
const UDF = `${BASE}/api/market/udf`

/** رزولوشن‌های تریدینگ‌ویو به ثانیه */
export const TV_RESOLUTIONS: Record<string, number> = {
  '1': 60, '5': 300, '15': 900, '30': 1800,
  '60': 3600, '120': 7200, '240': 14400, '360': 21600, '720': 43200,
  D: 86400, '1D': 86400, W: 604800, '1W': 604800, M: 2592000, '1M': 2592000,
}

export const resolutionToSeconds = (r: string): number =>
  TV_RESOLUTIONS[String(r).toUpperCase()] ?? TV_RESOLUTIONS[String(r)] ?? 3600

export type Bar = {
  time: number; open: number; high: number; low: number; close: number; volume: number
}

/**
 * پاسخ ستونیِ UDF را به کندل‌های کتابخانه تبدیل می‌کند.
 *
 * هر ردیفِ ناقص یا نامعتبر کنار گذاشته می‌شود؛ یک `null` وسط داده کافی است
 * تا کتابخانه کل بسته را دور بیندازد.
 */
export function barsFromUdf(j: any): Bar[] {
  if (!j || j.s !== 'ok' || !Array.isArray(j.t)) return []
  const out: Bar[] = []
  for (let i = 0; i < j.t.length; i++) {
    const raw = [j.t[i], j.o?.[i], j.h?.[i], j.l?.[i], j.c?.[i]]
    // `Number(null)` صفر می‌شود و صفر هم عددِ معتبری است؛ پس خالی‌بودن باید
    // **پیش از** تبدیل بررسی شود، وگرنه ردیفِ ناقص به‌شکل کندلی با قیمت صفر
    // وارد نمودار می‌شد.
    if (raw.some((v) => v == null || v === '')) continue
    const [t, o, h, l, c] = raw.map(Number)
    if (![t, o, h, l, c].every(Number.isFinite)) continue
    out.push({
      time: t * 1000, open: o, high: h, low: l, close: c,
      volume: Number(j.v?.[i]) || 0,
    })
  }
  out.sort((a, b) => a.time - b.time)
  return out
}

const CONFIG = {
  supported_resolutions: ['1', '5', '15', '30', '60', '120', '240', '360', '720', 'D', 'W', 'M'],
  supports_search: true,
  supports_group_request: false,
  supports_marks: false,
  supports_timescale_marks: false,
  supports_time: true,
  exchanges: [{ value: 'Arzinoo', name: 'Arzinoo', desc: 'ارزینو' }],
  symbols_types: [{ name: 'crypto', value: 'crypto' }],
}

const jget = async (path: string) => {
  const r = await fetch(`${UDF}${path}`)
  if (!r.ok) throw new Error(`udf ${r.status}`)
  return r.json()
}

export function createDatafeed(pollMs = 5000) {
  const subs = new Map<string, { timer: any }>()

  return {
    onReady(cb: (c: any) => void) { setTimeout(() => cb(CONFIG), 0) },

    searchSymbols(userInput: string, _ex: string, _type: string, onResult: (r: any[]) => void) {
      jget(`/search?query=${encodeURIComponent(userInput || '')}&limit=30`)
        .then((r) => onResult(Array.isArray(r) ? r : []))
        .catch(() => onResult([]))
    },

    resolveSymbol(symbolName: string, onResolve: (i: any) => void, onError: (e: string) => void) {
      jget(`/symbols?symbol=${encodeURIComponent(symbolName)}`)
        .then((info) => (info && info.s === 'error' ? onError('نماد یافت نشد') : onResolve(info)))
        .catch(() => onError('دریافت اطلاعات نماد ناموفق بود'))
    },

    getBars(
      symbolInfo: any, resolution: string, periodParams: any,
      onResult: (bars: Bar[], meta: { noData: boolean; nextTime?: number }) => void,
      onError: (e: string) => void,
    ) {
      const { from, to, countBack, firstDataRequest } = periodParams || {}
      const q = new URLSearchParams({
        symbol: symbolInfo?.ticker || symbolInfo?.name || '',
        resolution: String(resolution),
        from: String(Math.floor(from)),
        to: String(Math.floor(to)),
      })
      // درخواست اول را بدون countback می‌فرستیم تا نمودار با همان بازه‌ای
      // که خودش خواسته پر شود؛ درخواست‌های بعدی (اسکرول به عقب) دقیقاً
      // همان تعداد کندلی را می‌خواهند که کتابخانه می‌خواهد.
      if (countBack && !firstDataRequest) q.set('countback', String(countBack))
      jget(`/history?${q.toString()}`)
        .then((j) => {
          if (j?.s === 'no_data') {
            onResult([], { noData: true, ...(j.nextTime ? { nextTime: j.nextTime } : {}) })
            return
          }
          // محدودکردن به بازه‌ای که خواسته شده: کندلی بیرون از بازه، حتی
          // اگر سرور اشتباه بفرستد، نباید وارد نمودار شود.
          const lo = Math.floor(from) * 1000
          const hi = Math.floor(to) * 1000
          const all = barsFromUdf(j)
          const bars = countBack && !firstDataRequest
            ? all.filter((b) => b.time <= hi)
            : all.filter((b) => b.time >= lo && b.time <= hi)
          onResult(bars, { noData: bars.length === 0 })
        })
        .catch(() => onError('دریافت تاریخچه ناموفق بود'))
    },

    subscribeBars(
      symbolInfo: any, resolution: string, onTick: (b: Bar) => void, guid: string,
    ) {
      const sec = resolutionToSeconds(resolution)
      let lastTime = 0
      const poll = async () => {
        try {
          const now = Math.floor(Date.now() / 1000)
          const j = await jget(`/history?symbol=${encodeURIComponent(
            symbolInfo?.ticker || symbolInfo?.name || '')}&resolution=${resolution}` +
            `&from=${now - sec * 3}&to=${now}`)
          const bars = barsFromUdf(j)
          const last = bars[bars.length - 1]
          // فقط جلو می‌رویم: کندلی با زمانِ عقب‌تر، داده کتابخانه را خراب
          // می‌کند و نمودار از حرکت می‌ایستد.
          if (last && last.time >= lastTime) { lastTime = last.time; onTick(last) }
        } catch { /* شبکه قطع بود؛ دفعه بعد */ }
      }
      poll()
      subs.set(guid, { timer: setInterval(poll, pollMs) })
    },

    unsubscribeBars(guid: string) {
      const s = subs.get(guid)
      if (s) { clearInterval(s.timer); subs.delete(guid) }
    },

    getServerTime(cb: (t: number) => void) {
      fetch(`${UDF}/time`).then((r) => r.text())
        .then((t) => { const n = Number(t); if (Number.isFinite(n)) cb(n) })
        .catch(() => { /* شمارش معکوس اختیاری است */ })
    },
  }
}
